import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiFetch } from "@/lib/api";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn, type SortDirection } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailModal } from "@/components/detail-modal";
import { EntityForm } from "@/components/entity-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

interface CompanyDto { id: number; companyName: string; departmentName: string | null;
  accountsContactClientId: number | null; invoiceEmail: string | null;
  plainPackaging: boolean; hasApprovedCreditAccount: boolean; deleted: boolean; }
interface Paged<T> { items: T[]; totalCount: number; }

const schema = z.object({
  companyName: z.string().min(1).max(100),
  departmentName: z.string().max(100).nullable().optional(),
  accountsContactClientId: z.number().int().positive().nullable().optional(),
  invoiceEmail: z.string().email().or(z.literal("")).nullable().optional(),
  plainPackaging: z.boolean(),
  hasApprovedCreditAccount: z.boolean(),
});
type Form = z.infer<typeof schema>;

export function CompaniesPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [creating, setCreating] = useState(false);
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["companies", page], queryFn: () => apiFetch<Paged<CompanyDto>>(`/api/companies?page=${page}&pageSize=50`) });

  // Create-only: editing a company happens on its profile page (/companies/:id).
  const create = useMutation({
    mutationFn: (v: Form) => apiFetch<CompanyDto>("/api/companies", { method: "POST", body: JSON.stringify(v) }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["companies"] }); setCreating(false); },
  });

  const cols: DataGridColumn<CompanyDto>[] = [
    { id: "name",   header: "Company",       sortable: true, accessor: (r) => <button className="underline" onClick={() => navigate(`/companies/${r.id}`)}>{r.companyName}</button> },
    { id: "dept",   header: "Department",    sortable: true, accessor: (r) => r.departmentName ?? "—" },
    { id: "email",  header: "Invoice email", sortable: true, accessor: (r) => r.invoiceEmail ?? "—" },
    { id: "credit", header: "Credit",        sortable: true, accessor: (r) => r.hasApprovedCreditAccount ? "Yes" : "—" },
  ];

  // Sort state — client-side only; /api/companies has no `sort` param.
  // Sort applies to the current page's rows, not the full result set.
  const [sort, setSort] = useState<{ columnId: string; direction: SortDirection } | null>(null);

  const sortedRows = useMemo(() => {
    const rows = data?.items ?? [];
    if (!sort) return rows;
    const dir = sort.direction === "asc" ? 1 : -1;
    const valueFor = (r: CompanyDto): string | number | null => {
      switch (sort.columnId) {
        case "name":   return r.companyName;
        case "dept":   return r.departmentName ?? null;
        case "email":  return r.invoiceEmail ?? null;
        case "credit": return r.hasApprovedCreditAccount ? 1 : 0;
        default:       return null;
      }
    };
    return [...rows].sort((a, b) => {
      const va = valueFor(a);
      const vb = valueFor(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [data, sort]);

  return (
    <ListPage backTo="/admin" title="Companies" primaryAction={<IntentButton onClick={() => setCreating(true)}><Plus className="mr-2 h-4 w-4" /> Add Company</IntentButton>}>
      <DataGrid rows={sortedRows} columns={cols} rowKey={(r) => r.id}
        totalRows={data?.totalCount ?? 0} pageIndex={page} pageSize={50}
        onPageChange={setPage} onSortChange={setSort} />
      <DetailModal
        open={creating}
        onOpenChange={(o) => { if (!o) setCreating(false); }}
        title="New company">
        <EntityForm schema={schema} defaultValues={{ companyName: "", departmentName: "", accountsContactClientId: null,
          invoiceEmail: "", plainPackaging: false, hasApprovedCreditAccount: false }} onSubmit={(v) => create.mutate(v)}>
          {(f) => (
            <div className="space-y-3">
              <div><Label htmlFor="cn">Name</Label><Input id="cn" {...f.register("companyName")} /></div>
              <div><Label htmlFor="dn">Department</Label><Input id="dn" {...f.register("departmentName")} /></div>
              <div><Label htmlFor="iv">Invoice email</Label><Input id="iv" type="email" {...f.register("invoiceEmail")} /></div>
              <div><Label htmlFor="ac">Accounts contact client ID</Label><Input id="ac" type="number" {...f.register("accountsContactClientId", { setValueAs: (v) => (v === "" || v == null ? null : Number(v)) })} /></div>
              <label className="flex items-center gap-2"><input type="checkbox" {...f.register("plainPackaging")} /> Plain packaging</label>
              <label className="flex items-center gap-2"><input type="checkbox" {...f.register("hasApprovedCreditAccount")} /> Approved credit account</label>
              <Button type="submit" disabled={create.isPending}>Create</Button>
            </div>
          )}
        </EntityForm>
      </DetailModal>
    </ListPage>
  );
}
